import _extends from "@babel/runtime/helpers/extends";
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import Icon from '../Icon';
import StyledButton, { buttonSizeProps } from './styles';
import * as Sizes from './sizes';
import * as Emphasis from './emphasis';
import IconSizes from './icon-sizes';
import Link, { linkAsButton } from '../Link';
import useAutofocus from '../hooks/useAutofocus';
import Spinner from '../Spinner';
function determineType({
  href,
  submit,
  tertiary,
  buttonProps
}) {
  if (buttonProps?.type) {
    return buttonProps.type;
  }
  if (href || tertiary) {
    return undefined;
  }
  if (submit) {
    return 'submit';
  }
  return 'button';
}
const Button = /*#__PURE__*/forwardRef(({
  autofocus,
  children,
  className = null,
  onClick = null,
  disabled = false,
  size = Sizes.normal,
  emphasis = Emphasis.primary,
  destructive,
  fullWidth = false,
  href,
  submit = false,
  filter = false,
  buttonProps = {},
  icon,
  alignIcon = 'start',
  iconProps = {},
  'data-auto': dataAuto,
  justifyBetween = false,
  anchorComponent = Link,
  loading = false,
  reverseFocus = false,
  ...props
}, ref) => {
  const theme = useTheme();
  const combineRefs = useAutofocus({
    autofocus
  });
  const tertiary = emphasis === Emphasis.tertiary;
  const onClickFn = disabled || buttonProps?.disabled ? null : onClick;
  const isLink = href || tertiary;
  const anchorOverride = isLink ? anchorComponent : undefined;
  return /*#__PURE__*/React.createElement(StyledButton, _extends({
    ref: combineRefs([ref]),
    "data-size": size,
    "data-full-width": fullWidth,
    "data-auto": dataAuto,
    type: determineType({
      href,
      submit,
      tertiary,
      buttonProps
    })
  }, props, {
    className,
    onClick: onClickFn,
    disabled: disabled || loading,
    href,
    $alignIcon: alignIcon,
    $emphasis: emphasis,
    $destructive: destructive,
    $isFilter: filter,
    $justifyBetween: justifyBetween,
    $loading: loading,
    $reverseFocus: reverseFocus,
    as: anchorOverride
  }, buttonProps, isLink ? linkAsButton({
    ...buttonProps,
    href,
    onClick: onClickFn
  }) : null), /*#__PURE__*/React.createElement("span", {
    className: "contentWrapper"
  }, icon && alignIcon === 'start' ? /*#__PURE__*/React.createElement(Icon, _extends({
    icon: icon,
    size: theme[IconSizes[size]]
  }, iconProps)) : null, children, icon && alignIcon === 'end' ? /*#__PURE__*/React.createElement(Icon, _extends({
    icon: icon,
    size: theme[IconSizes[size]]
  }, iconProps)) : null), loading ? /*#__PURE__*/React.createElement(Spinner, {
    height: theme[buttonSizeProps[size].lineHeight],
    width: theme[buttonSizeProps[size].lineHeight],
    color: emphasis === Emphasis.primary ? theme.palette.contentReverse : theme.palette.contentDisabled
  }) : null);
});
Button.propTypes = {
  children: PropTypes.node,
  emphasis: PropTypes.oneOf(Object.keys(Emphasis)),
  destructive: PropTypes.bool,
  size: PropTypes.oneOf(Object.keys(Sizes)),
  onClick: PropTypes.func,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  submit: PropTypes.bool,
  filter: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  buttonProps: PropTypes.object,
  href: PropTypes.string,
  icon: PropTypes.node,
  alignIcon: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  iconProps: PropTypes.object,
  'data-auto': PropTypes.string,
  justifyBetween: PropTypes.bool,
  anchorComponent: PropTypes.elementType,
  loading: PropTypes.bool,
  reverseFocus: PropTypes.bool
};
Button.injectFormProps = true;
export { Sizes, Emphasis };
export default Button;
//# sourceMappingURL=index.js.map